/**
 * 官网公共增强入口（所有前台页引入 / 由服务端自动注入 motion.js）：
 * - 页面入场（壳层滑入 + 分批 reveal）— 新页自动具备
 * - 备案页脚
 * - 滚动侧栏质感
 * - 视口补入场（榜单等，等壳层落稳后再挂，避免叠位移）
 */
import { PAGE_ENTER_TIMING } from '@/config/preload';
import { mountSiteFooter } from '@/components/common/site-footer';
import { bindPageEnterOnShow, mountPageEnter } from '@/motion/page-enter';
import { prefersReducedMotion } from '@/utils/motion-pref';
import { withFxParam } from '@/router/paths';

const REVEAL_SEL =
  '.rank-hero, .rank-form .field, .rank-stage, .rank-podium, .rank-rest-wrap, .rank-shot-preview';

function onScroll(): void {
  document.body.classList.toggle('is-scrolled', window.scrollY > 12);
}

function tagReveal(el: HTMLElement, index: number): void {
  if (el.classList.contains('motion-reveal') || el.hasAttribute('data-reveal')) return;
  if (el.closest('#playGrid, #sideFunList, #probabilityGrid, .brand-rail')) return;
  el.classList.add('motion-reveal');
  el.style.setProperty('--motion-stagger', `${Math.min(index, 8) * 0.04}s`);
}

function collectAndTag(root: ParentNode = document): HTMLElement[] {
  const list = Array.from(root.querySelectorAll<HTMLElement>(REVEAL_SEL));
  list.forEach((el, i) => tagReveal(el, i));
  return list.filter((el) => el.classList.contains('motion-reveal'));
}

function bootReveal(): void {
  if (prefersReducedMotion()) {
    document.documentElement.classList.add('motion-reduce');
    document.querySelectorAll('.motion-reveal').forEach((el) => {
      el.classList.add('motion-in');
    });
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        el.classList.add('motion-in');
        io.unobserve(el);
      }
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.12 },
  );

  const observeAll = (root: ParentNode = document) => {
    collectAndTag(root).forEach((el) => {
      if (el.classList.contains('motion-in')) return;
      io.observe(el);
    });
  };

  observeAll();

  const mo = new MutationObserver(() => observeAll());
  for (const id of ['playGrid', 'sideFunList', 'probabilityGrid', 'rbList', 'rankRoot']) {
    const box = document.getElementById(id);
    if (box) mo.observe(box, { childList: true, subtree: true });
  }
}

function bindFxAwareLinks(): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a) => {
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('http'))
      return;
    if (href.includes('fx=1')) return;
    if (
      href.endsWith('.html') ||
      href.startsWith('/rank') ||
      href.startsWith('index') ||
      href.startsWith('play')
    ) {
      a.setAttribute('href', withFxParam(href));
    }
  });
}

function boot(): void {
  mountPageEnter();
  bindPageEnterOnShow();
  bindFxAwareLinks();
  void mountSiteFooter();
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  // 壳层落稳后再挂视口 reveal，避免与 paper 滑入叠位移
  const delay = prefersReducedMotion() ? 0 : PAGE_ENTER_TIMING.revealLeadMs;
  window.setTimeout(() => bootReveal(), delay);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
